import { CloseCircleOutlined, DollarOutlined, SyncOutlined, HistoryOutlined } from "@ant-design/icons";
import { Button, Modal } from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getTodaysInvoicesByType, getTodaysPendingInvoices, getInvoicesByType } from "../../services/invoiceService";
import AperturaDeCajaForm from "../AperturaDeCaja/AperturaDeCajaForm";
import CierreDeCajaForm from "../CierreDeCaja/CierreDeCajaForm";
import TraerHistoricosForm from "../SincronizarHistoricos/TraerHistoricosForm";
import { useConfig } from "../../contexts/ConfigContext";
import { useAuth } from "../../contexts/AuthContext";
import { isCajero } from "../../config/rbac/rbac.config";
import "./Dashboard.css";
import { getDashboardConfig } from "./dashboard.config";
import PageSpinner from "../Spinner/PageSpinner";

const Dashboard = () => {
	const { country, loading } = useConfig();
	const { currentUser } = useAuth();
	const cajero = isCajero(country?.code, currentUser?.Admin);
	const { showAbonos, showHistoricoAbonos } = getDashboardConfig(country?.code);
	const [refreshKey, setRefreshKey] = useState(0);
	const [invoicesByType, setInvoicesByType] = useState({
		invoices: [],
		creditMemos: [],
		pending: [],
	});

	const [historicalData, setHistoricalData] = useState({
		allInvoices: [],
		allCreditMemos: [],
	});

	const [aperturaCajaModalVisible, setAperturaCajaModalVisible] =
		useState(false);
	const [cierreCajaModalVisible, setCierreCajaModalVisible] = useState(false);
	const [traerHistoricosModalVisible, setTraerHistoricosModalVisible] = useState(false);
	const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
	const navigate = useNavigate();

	const handleHistoricosSynced = async () => {
		try {
			// vuelves a cargar desde IndexedDB usando tu servicio
			const allInvoices = await getInvoicesByType("Invoice");
			const allCreditMemos = await getInvoicesByType("Credit Memo");

			setHistoricalData({
				allInvoices,
				allCreditMemos,
			});
		} catch (error) {
			console.error("Error al refrescar datos históricos:", error);
		}
	};

	// Detectar si es dispositivo móvil
	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth < 768);
		};

		window.addEventListener("resize", handleResize);
		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	useEffect(() => {
		const fetchData = async () => {
			try {
				// Obtener facturas de hoy por tipo
				const regularInvoices = await getTodaysInvoicesByType("Invoice");
				const creditMemos = await getTodaysInvoicesByType("Credit Memo");
				const pendingInvoices = await getTodaysPendingInvoices();

				setInvoicesByType({
					invoices: regularInvoices,
					creditMemos: creditMemos,
					pending: pendingInvoices,
				});

				// Obtener datos históricos (todas las facturas)
				handleHistoricosSynced();
			} catch (error) {
				console.error("Error al cargar el dashboard:", error);
			}
		};

		fetchData();
	}, [refreshKey]);

	const handleCardClick = () => {
		navigate("/facturas?filter=today");
	};

	const handleCreditMemoCardClick = () => {
		navigate("/abonos?filter=today");
	};

	const handlePendingCardClick = () => {
		navigate("/facturas-pendientes");
	};

	const handleHistoricalInvoicesClick = () => {
		navigate("/facturas");
	};

	const handleHistoricalCreditMemosClick = () => {
		navigate("/abonos");
	};

	const showAperturaCajaModal = () => {
		setAperturaCajaModalVisible(true);
	};

	const handleAperturaCajaModalClose = () => {
		setAperturaCajaModalVisible(false);
	};

	const handleAperturaAdded = (result) => {
		// Cerrar el modal después de registrar exitosamente
		setAperturaCajaModalVisible(false);
	};

	const showCierreCajaModal = () => {
		setCierreCajaModalVisible(true);
	};

	const handleCierreCajaModalClose = () => {
		setCierreCajaModalVisible(false);
	};

	const handleCierreAdded = (result) => {
		// Cerrar el modal después de registrar exitosamente
		setCierreCajaModalVisible(false);
	};

	const handleTraerHistoricosModal = (open) => {
		setTraerHistoricosModalVisible(open);
	}

	return (
		<div className="dashboard-container">
			<h2>Bienvenido a ExceliaOne</h2>
			<p>Sistema POS con soporte offline y gestión multinacional</p>

			{/* Componente de información de almacenamiento */}
			{loading ? <PageSpinner /> :
				<>
					{/* Botones de Abrir y Cerrar Caja */}
					<div
						className="dashboard-header"
						style={{
							display: "flex",
							flexDirection: isMobile ? "column" : "row",
							justifyContent: "space-between",
							alignItems: isMobile ? "flex-start" : "center",
							gap: isMobile ? "10px" : "0",
						}}
					>
						<h3>Documentos de Hoy</h3>
						<div
							style={{
								display: "flex",
								gap: "10px",
								width: isMobile ? "100%" : "auto",
								justifyContent: isMobile ? "space-between" : "flex-end",
							}}
						>
							<Button
								type="primary"
								icon={<DollarOutlined />}
								onClick={showAperturaCajaModal}
								className="open-cash-button"
								style={{
									flex: isMobile ? "1" : "none",
								}}
							>
								Abrir Caja
							</Button>
							<Button
								type="primary"
								icon={<CloseCircleOutlined />}
								onClick={showCierreCajaModal}
								className="close-cash-button"
								style={{
									flex: isMobile ? "1" : "none",
								}}
							>
								Cerrar Caja
							</Button>
							{!cajero && (
							<Button
								type="primary"
								icon={<HistoryOutlined />}
								className="sync-history-button"
								style={{
									flex: isMobile ? "1" : "none",
								}}
								onClick={() => { handleTraerHistoricosModal(true) }}
							>
								Traer históricos
							</Button>
							)}
						</div>
					</div>
					<div className="card-container">
						<div className="bc-card" onClick={handleCardClick}>
							<div className="bc-card-title">Facturas</div>
							<div className="bc-card-content">
								<div className="bc-card-number">
									{invoicesByType.invoices.length}
								</div>
							</div>
							<div className="bc-card-footer">
								<div className="bc-card-arrow">›</div>
							</div>
						</div>

						{showAbonos && !cajero && (
							< div className="bc-card" onClick={handleCreditMemoCardClick}>
								<div className="bc-card-title">Abonos</div>
								<div className="bc-card-content">
									<div className="bc-card-number">
										{invoicesByType.creditMemos.length}
									</div>
								</div>
								<div className="bc-card-footer">
									<div className="bc-card-arrow">›</div>
								</div>
							</div>
						)}

						{!cajero && (
						<div className="bc-card bc-card-pending" onClick={handlePendingCardClick}>
							<div className="bc-card-title">
								<SyncOutlined style={{ marginRight: '8px' }} />
								Pendientes
							</div>
							<div className="bc-card-content">
								<div className="bc-card-number">
									{invoicesByType.pending.length}
								</div>
							</div>
							<div className="bc-card-footer">
								<div className="bc-card-arrow">›</div>
							</div>
						</div>
						)}
					</div>

					{/* Sección Histórico */}
					<div
						className="dashboard-header"
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							marginTop: "30px",
						}}
					>
						<h3>Histórico</h3>
					</div>
					<div className="card-container">
						<div className="bc-card bc-card-historical" onClick={handleHistoricalInvoicesClick}>
							<div className="bc-card-title">
								<HistoryOutlined style={{ marginRight: '8px' }} />
								Histórico Facturas
							</div>
							<div className="bc-card-content">
								<div className="bc-card-number">
									{historicalData.allInvoices.length}
								</div>
							</div>
							<div className="bc-card-footer">
								<div className="bc-card-arrow">›</div>
							</div>
						</div>

						{showHistoricoAbonos && !cajero && (
							<div className="bc-card bc-card-historical" onClick={handleHistoricalCreditMemosClick}>
								<div className="bc-card-title">
									<HistoryOutlined style={{ marginRight: '8px' }} />
									Histórico Abonos
								</div>
								<div className="bc-card-content">
									<div className="bc-card-number">
										{historicalData.allCreditMemos.length}
									</div>
								</div>
								<div className="bc-card-footer">
									<div className="bc-card-arrow">›</div>
								</div>
							</div>
						)}
					</div>

					{/* Modal para el formulario de apertura de caja */}
					<Modal
						title="Apertura de Caja"
						open={aperturaCajaModalVisible}
						onCancel={handleAperturaCajaModalClose}
						footer={null}
						width={isMobile ? "95%" : 700}
					>
						<AperturaDeCajaForm
							onAperturaAdded={handleAperturaAdded}
							onAperturaUpdated={() => { }}
							onRefreshDashboard={() => setRefreshKey(prev => prev + 1)}
						/>
					</Modal>

					{/* Modal para el formulario de cierre de caja */}
					<Modal
						title="Cierre de Caja"
						open={cierreCajaModalVisible}
						onCancel={handleCierreCajaModalClose}
						footer={null}
						width={isMobile ? "95%" : 700}
						destroyOnHidden
					>
						<CierreDeCajaForm
							onSave={handleCierreAdded}
							onCancel={handleCierreCajaModalClose}
						/>
					</Modal>

					{/* Modal para el formulario de traer historicos */}
					<Modal
						title="Traer históricos"
						open={traerHistoricosModalVisible}
						onCancel={() => { setTraerHistoricosModalVisible(false) }}
						footer={null}
						width={isMobile ? "95%" : 700}
						destroyOnHidden
					>
						<TraerHistoricosForm
							handleCancel={() => { setTraerHistoricosModalVisible(false) }}
							handleHistoricosSynced={handleHistoricosSynced}
							onRefreshDashboard={() => setRefreshKey(prev => prev + 1)}
							country={country}
						/>
					</Modal>
				</>}
		</div >

	);
};

export default Dashboard;
